import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Loader2, Plus, Pencil, Trash2, Check, X, Shield, Building } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  reponse: 'bg-blue-500/10 text-blue-700 border-blue-200',
  style: 'bg-violet-500/10 text-violet-700 border-violet-200',
  transparence: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
  communication: 'bg-amber-500/10 text-amber-700 border-amber-200',
  general: 'bg-zinc-500/10 text-zinc-600 border-zinc-200',
};

export function PolicyEditor({
  title,
  description,
  icon: Icon = Shield,
  fetchUrl,
  addUrl,
  updateUrl,
  deleteUrl,
  token,
  readOnly = false,
}) {
  const [rules, setRules] = useState([]);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newRule, setNewRule] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}${fetchUrl}`, { headers: { Authorization: `Bearer ${token}` } });
      setRules(res.data.rules || []);
      setVersion(res.data.version || 0);
    } catch (e) {
      toast.error('Erreur de chargement de la politique');
    } finally {
      setLoading(false);
    }
  }, [fetchUrl, token]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleAdd = async () => {
    if (!newRule.trim()) return;
    try {
      await axios.post(`${API}${addUrl}`, { rule: newRule.trim(), category: newCategory }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Règle ajoutée');
      setNewRule('');
      setAddOpen(false);
      fetchRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggle = async (ruleId, active) => {
    try {
      await axios.patch(`${API}${updateUrl}/${ruleId}`, { active }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchRules();
    } catch (e) {
      toast.error('Erreur de mise à jour');
    }
  };

  const handleEdit = async (ruleId) => {
    if (!editText.trim()) return;
    try {
      await axios.patch(`${API}${updateUrl}/${ruleId}`, { rule: editText.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Règle modifiée');
      setEditId(null);
      fetchRules();
    } catch (e) {
      toast.error('Erreur de modification');
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await axios.delete(`${API}${deleteUrl}/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Règle supprimée');
      fetchRules();
    } catch (e) {
      toast.error('Erreur de suppression');
    }
  };

  return (
    <Card data-testid="policy-editor">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Icon className="w-4 h-4 text-blue-500" />
              {title}
            </CardTitle>
            <CardDescription className="text-xs mt-1">{description}</CardDescription>
          </div>
          <Badge variant="outline" className="text-[10px]" data-testid="policy-version">v{version}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin" /></div>
        ) : rules.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-3" data-testid="policy-empty">
            Aucune règle configurée
          </p>
        ) : (
          rules.map((rule, idx) => (
            <div key={rule.id} className={`flex items-start gap-2 py-2 border-b last:border-0 ${!rule.active ? 'opacity-50' : ''}`} data-testid={`policy-rule-${rule.id}`}>
              <span className="text-xs text-muted-foreground mt-1.5 w-5 shrink-0">{idx + 1}.</span>
              <div className="flex-1 min-w-0">
                {editId === rule.id ? (
                  <div className="flex gap-1">
                    <Input value={editText} onChange={e => setEditText(e.target.value)} className="h-7 text-sm flex-1" autoFocus data-testid="edit-rule-input" />
                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => handleEdit(rule.id)} data-testid="save-edit-btn"><Check className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditId(null)}><X className="w-3 h-3" /></Button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm leading-snug">{rule.rule}</p>
                    {rule.category && rule.category !== 'general' && (
                      <Badge variant="outline" className={`text-[9px] mt-1 ${CATEGORY_COLORS[rule.category] || CATEGORY_COLORS.general}`}>
                        {rule.category}
                      </Badge>
                    )}
                  </>
                )}
              </div>
              {!readOnly && editId !== rule.id && (
                <div className="flex items-center gap-1 shrink-0">
                  <Switch checked={rule.active} onCheckedChange={v => handleToggle(rule.id, v)} data-testid={`toggle-rule-${rule.id}`} />
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditId(rule.id); setEditText(rule.rule); }} data-testid={`edit-rule-${rule.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => handleDelete(rule.id)} data-testid={`delete-rule-${rule.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ))
        )}

        {/* Add rule */}
        {!readOnly && (
          <>
            {addOpen ? (
              <div className="space-y-2 pt-2" data-testid="add-rule-form">
                <Input placeholder="Nouvelle règle..." value={newRule} onChange={e => setNewRule(e.target.value)} className="h-8 text-sm" data-testid="new-rule-input" />
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    {['general', 'reponse', 'style', 'transparence', 'communication'].map(cat => (
                      <button key={cat} onClick={() => setNewCategory(cat)}
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${newCategory === cat ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                        {cat}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setAddOpen(false)}>Annuler</Button>
                    <Button size="sm" className="text-xs h-7" onClick={handleAdd} data-testid="save-rule-btn">Ajouter</Button>
                  </div>
                </div>
              </div>
            ) : (
              <Button variant="outline" size="sm" className="w-full text-xs gap-1 mt-2" onClick={() => setAddOpen(true)} data-testid="add-rule-btn">
                <Plus className="w-3.5 h-3.5" /> Ajouter une règle
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
